import { useRef, useEffect } from 'react'
import { BrowseTabData } from '../../../types/peer-analyser'
import { lightImpactVibrate } from '../../../utils/appBridge'

interface BrowseCategoriesTabsProps {
  tabs: BrowseTabData[]
  selectedTabId: string
  onTabSelect: (tabId: string) => void
}

export default function BrowseCategoriesTabs({ tabs, selectedTabId, onTabSelect }: BrowseCategoriesTabsProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Function to scroll clicked item to middle of screen
  const scrollToMiddle = (clickedElement: HTMLElement) => {
    if (!menuRef.current) return

    const menuContainer = menuRef.current
    const containerWidth = menuContainer.clientWidth
    const elementLeft = clickedElement.offsetLeft
    const elementWidth = clickedElement.offsetWidth

    // Calculate the scroll position to center the element
    const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2

    // Smooth scroll to the calculated position
    menuContainer.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    // Scroll to selected tab on mount or when selection changes
    if (menuRef.current && selectedTabId) {
      const selectedElement = menuRef.current.querySelector(`[data-tab-id="${selectedTabId}"]`) as HTMLElement
      if (selectedElement) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          scrollToMiddle(selectedElement)
        }, 100)
      }
    }
  }, [selectedTabId])

  if (!tabs?.length) {
    return null
  }

  return (
    <div
      ref={menuRef}
      className='flex flex-row flex-shrink-0 gap-0 min-w-16 px-4 overflow-x-auto border-b-[1px] border-grey60 no-scrollbar'
    >
      {tabs.map((tab) => {
        if (!tab?.id) return null

        const isSelected = selectedTabId === tab.id

        return (
          <div
            key={tab.id}
            data-tab-id={tab.id}
            role='button'
            tabIndex={0}
            className={`flex items-center justify-center px-4 py-3 cursor-pointer focus:outline-none transition-colors relative
                            ${isSelected ? 'text-[#232323] font-medium' : 'text-[#7c7e80]'}
                        `}
            onClick={(e) => {
              lightImpactVibrate()
              if (tab?.id) {
                onTabSelect(tab.id)
                scrollToMiddle(e.currentTarget)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                lightImpactVibrate()
                if (tab?.id) {
                  onTabSelect(tab.id)
                  scrollToMiddle(e.currentTarget)
                }
              }
            }}
          >
            {tab?.label && <span className='text-[15px] whitespace-nowrap'>{tab.label}</span>}
            {isSelected && <div className='absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-[#232323]' />}
          </div>
        )
      })}
    </div>
  )
}
